import { fetchWithChatFeedback } from './chatFeedbackRequired';
import { apiCall, type ApiResponse } from './apiClient';
import { extractDetailMessage } from '../utils/userFacingError';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/** Deliberately draft-only client seam; endpoint paths may be wired by the API phase. */
export interface PostAwardWorkspaceSummary {
  id: string;
  action_id?: string;
  workspace_id?: string;
  flow_id?: string;
  flow_type?: 'post_award_actions';
  // List/restore responses are server-shaped (`entry_mode`); this legacy
  // camelCase field is only populated by callers that construct a summary.
  entryMode?: 'tracker' | 'package';
  contractNumber?: string;
  workflowFlowId?: string;
}

/** Public, restorable workspace shape. Private extracted text/storage keys stay server-side. */
export interface PostAwardSourceDocument {
  document_id: string;
  document_type: PostAwardDocumentType;
  status: 'available' | 'missing' | 'conflicting' | 'needs_human_resolution';
  provenance: 'user_supplied' | 'tracker_reference';
  filename?: string;
}

export interface PostAwardWorkspace extends PostAwardWorkspaceSummary {
  action_id: string;
  workspace_id: string;
  flow_id: string;
  entry_mode: 'tracker' | 'package';
  contract_identity?: { contract_number?: string } | null;
  document_labels: string[];
  source_documents: PostAwardSourceDocument[];
  narrative?: string | null;
  recommended_action?: 'modification' | 'option_exercise' | 'recompete' | 'connit_handoff' | 'needs_clarification' | null;
  user_selected_action?: 'modification' | 'option_exercise' | 'recompete' | 'connit_handoff' | 'needs_clarification' | null;
  route_state?: 'modification' | 'option_exercise' | 'recompete' | 'connit_handoff' | 'needs_clarification' | null;
  routing_reason?: string | null;
  recommendation_next_question?: string | null;
  recommendation_notice?: string | null;
  recommendation_error?: string | null;
  recommendation_answer?: string | null;
  route_blockers?: string[];
  route_confirmation_blockers?: string[];
  connit_handoff?: { required: true; message: string; return_target?: string } | null;
  option_support?: Record<string, unknown> | null;
  option_support_request?: Record<string, unknown> | null;
  option_planning?: OptionPlanningWorkspace | null;
  modification_facts?: ModificationFacts | null;
  modification_package?: DraftPackage | null;
  recompete_context?: RecompeteContext | null;
  successor_flow_id?: string | null;
}

export type OptionPlanningStep = 'target_terms' | 'select_working_drafts' | 'complete_drafts' | 'review_package';
export type OptionPlanningModuleStatus = 'not_started' | 'in_progress' | 'complete' | 'blocked' | 'optional' | 'not_applicable' | 'unavailable_in_configuration' | 'awaiting_terms' | 'needs_human_resolution';
export type OptionPlanningFieldOrigin = 'suggested' | 'confirmed' | 'user_authored' | 'missing' | 'conflicting';
export type OptionPlanningDocumentRole = 'governing_award' | 'option_terms_schedule' | 'price_clin_schedule' | 'current_pws_sow' | 'prior_modification' | 'performance_cor_qap' | 'wage_support' | 'insurance_requirement' | 'insurance_certificate' | 'acp_recompete_context';

/** Safe server projection only: it deliberately excludes raw reference-template text. */
export interface OptionPlanningFieldDefinition {
  field_id: string;
  section_id?: string;
  label: string;
  field_type: 'text' | 'textarea' | 'date' | 'boolean' | 'select';
  required?: boolean;
  allowed_values?: string[];
  placeholder?: string;
  display_when?: { field_id: string; equals: string };
  /** Audit-only location in the supplied local example; never a policy source. */
  source_form_mapping?: { filename: string; page: string; section: string; form_label: string };
}
export interface OptionPlanningSectionDefinition { section_id: string; label: string; required_when_shown?: boolean; display_when: { field_id: string; equals: string }; }
/** Safe, server-controlled form hierarchy; never raw reference-template prose. */
export interface OptionPlanningFormSectionDefinition {
  section_id: string;
  label: string;
  guidance: string;
  source_form_section?: string;
  display_when?: { field_id: string; equals: string };
}
export interface OptionPlanningModuleDefinition {
  module_id: string;
  display_name: string;
  document_style?: 'structured' | 'memorandum' | 'memorandum_worksheet' | 'determination' | 'information_table';
  draft_only_notice: string;
  availability?: 'optional' | 'required';
  scenario?: 'options_remaining' | 'no_options_remaining' | 'any';
  policy_gate?: 'none' | 'organization_approved';
  required_source_roles?: OptionPlanningDocumentRole[];
  source_form_mapping?: { filename: string; pages: string; form_title: string };
  sections?: OptionPlanningFormSectionDefinition[];
  fields?: OptionPlanningFieldDefinition[];
  conditional_sections?: OptionPlanningSectionDefinition[];
}
export interface OptionPlanningConfigurationProjection {
  configuration_id: string;
  version: string;
  status: 'provisional' | 'organization_approved' | 'configuration_unavailable';
  action: 'option_exercise' | 'modification';
  owner?: string;
  timelines?: Array<{ timeline_id: '30_day' | '60_day' | 'custom'; display_name: string; explanatory_text: string; requires_contract_specific_basis_confirmation?: boolean }>;
  modules: OptionPlanningModuleDefinition[];
}
export interface OptionPlanningCandidate { value: string; document_id: string; source_version?: string | null; source_date?: string | null; source_status: string; status: 'suggested' | 'confirmed' | 'missing' | 'conflicting'; }
export interface OptionPlanningFieldValue { field_id: string; value?: string | null; origin: OptionPlanningFieldOrigin; candidates: OptionPlanningCandidate[]; client_edit_revision: number; server_field_revision: number; updated_at?: string | null; }
export interface OptionPlanningGeneratedArtifact {
  artifact_id: string;
  module_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  invalidated: boolean;
  dependency_workspace_revision: number;
  /** Empty means a legacy export that must be regenerated before download. */
  renderer_version?: string;
}
/** A prepare response identifies the exact fresh artifact to download.
 * `generated_artifacts` is historical workspace state and must not be used to
 * select a download after rendering. */
export interface OptionPlanningPrepareWorkspace extends OptionPlanningWorkspace {
  prepared_artifact: OptionPlanningGeneratedArtifact;
}
export interface OptionPlanningWorkspace {
  schema_version: 1;
  workspace_revision: number;
  configuration: { configuration_id: string; version: string; status: 'provisional' | 'organization_approved' | 'configuration_unavailable'; action: 'option_exercise' | 'modification' };
  /** Returned by newer API servers; old servers may omit it during rollout. */
  configuration_projection?: OptionPlanningConfigurationProjection;
  active_step: OptionPlanningStep;
  active_step_client_edit_revision: number;
  target: { contract_identifier?: string | null; option_identifier?: string | null; clin?: string | null; end_date?: string | null; timeline_id?: '30_day' | '60_day' | 'custom' | null; contract_specific_timeline_basis_confirmed: boolean };
  target_client_edit_revision: number;
  options_remaining: 'yes' | 'no' | 'unknown';
  options_remaining_client_edit_revision: number;
  document_roles: Array<{ document_id: string; role: OptionPlanningDocumentRole; client_edit_revision: number }>;
  modules: Array<{ module_id: string; selected: boolean; status: OptionPlanningModuleStatus; client_edit_revision: number }>;
  fields: OptionPlanningFieldValue[];
  conditional_sections: Array<{ module_id: string; section_id: string; included: 'unresolved' | 'included' | 'not_applicable'; client_edit_revision: number }>;
  blockers: Array<{ code: string; message: string; module_id?: string | null; field_id?: string | null }>;
  generated_artifacts: OptionPlanningGeneratedArtifact[];
  review_acknowledgements: Array<{ artifact_id: string; module_id: string; human_review_warning_acknowledged: true }>;
  no_options_context: { continuing_need: 'yes' | 'no' | 'unknown'; selected_for_recompete_handoff: boolean; selected_document_ids: string[]; successor_timing?: string | null; client_edit_revision: number };
  insurance_company_rows: Array<{ row_id: string; name: string; address: string; policy_number: string; telephone_number: string; email_address: string }>;
}

export type OptionPlanningUpdate =
  | { kind: 'target'; client_edit_revision: number; contract_identifier?: string; option_identifier?: string; clin?: string; end_date?: string; timeline_id?: '30_day' | '60_day' | 'custom'; contract_specific_timeline_basis_confirmed?: boolean }
  | { kind: 'options_remaining'; client_edit_revision: number; value: 'yes' | 'no' | 'unknown' }
  | { kind: 'document_role'; client_edit_revision: number; document_id: string; role: OptionPlanningDocumentRole }
  | { kind: 'module'; client_edit_revision: number; module_id: string; selected: boolean }
  | { kind: 'field'; client_edit_revision: number; field_id: string; value?: string; candidate_document_id?: string; confirmation?: 'suggest_candidate' | 'accept_candidate' | 'user_authored' | 'leave_unresolved' }
  | { kind: 'conditional_section'; client_edit_revision: number; module_id: string; section_id: string; included: 'included' | 'not_applicable' }
  | { kind: 'active_step'; client_edit_revision: number; value: OptionPlanningStep }
  | { kind: 'no_options_context'; client_edit_revision: number; value: 'yes' | 'no' | 'unknown'; selected_for_recompete_handoff?: boolean; selected_document_ids?: string[]; successor_timing?: string }
  | { kind: 'insurance_company_rows'; client_edit_revision: number; insurance_company_rows: Array<{ row_id: string; name: string; address: string; policy_number: string; telephone_number: string; email_address: string }> };

export interface OptionPlanningPatchInput {
  expectedWorkspaceRevision: number;
  updates: OptionPlanningUpdate[];
  configurationId?: string;
  configurationVersion?: string;
}

export interface CreatePostAwardWorkspaceInput {
  entryMode: 'tracker' | 'package';
  contractNumber?: string;
  trackerFlowId?: string;
  documentLabels?: string[];
  workflowFlowId?: string;
}

export type PostAwardDocumentType =
  | 'award_contract' | 'pws_sow' | 'prior_modification' | 'justification' | 'other';

export interface PostAwardDraftInput {
  entryMode?: 'tracker' | 'package';
  contractNumber?: string;
  documentLabels?: string[];
  narrative?: string;
  selectedAction?: 'modification' | 'option_exercise' | 'recompete' | 'connit_handoff' | 'needs_clarification';
  recommendationAnswer?: string;
}

/** Strictly draft-only scope/PWS facts accepted by the action workspace. */
export interface ModificationFacts {
  modification_type?: 'scope_pws_change';
  changed_need: string;
  proposed_scope_change: string;
  requested_effective_date: string;
  pop_extension_requested?: boolean;
  pop_impact?: string;
  baseline_resolution?: 'confirmed' | 'needs_human_resolution' | 'conflicting';
  baseline_document_ids?: string[];
  justification_items?: string[];
}

export interface DraftPackage {
  can_generate?: boolean;
  status?: string;
  watermark?: string;
  blockers?: string[];
  source_document_ids?: string[];
  artifacts?: Array<{ name?: string; filename?: string; content?: string; download_url?: string }>;
  connit_handoff?: { required?: boolean; message?: string };
}

export interface RecompeteContext {
  selected_document_ids?: string[];
  successor_timing?: string;
  confirmed_context?: boolean;
  missing_items?: string[];
}

export interface OptionSupportInput {
  target_period_id: string;
  timeline_point: string;
  option_periods: Array<Record<string, unknown>>;
  governing_terms_document_id?: string;
  governing_terms_option_end_date?: string;
  completed_items?: Record<string, Record<string, unknown>>;
  review_acknowledged?: boolean;
}

export interface RecompeteHandoffInput {
  confirmed_context: true;
  changed_need: string;
  current_pws_sow_document_id: string;
  selected_document_ids?: string[];
  successor_timing?: string;
  idempotency_key?: string;
}

export interface PostAwardDocumentUpload {
  filename: string;
  extraction_status: 'uploading' | 'extracting' | 'extracted' | 'failed';
  provenance: string;
  document_id?: string;
}

const workspacePath = (id: string) => `/api/post-award-actions/${encodeURIComponent(id)}`;

export const createPostAwardWorkspace = (input: CreatePostAwardWorkspaceInput): Promise<ApiResponse<PostAwardWorkspace>> =>
  apiCall('/api/post-award-actions', 'POST', {
    entry_mode: input.entryMode,
    contract_number: input.contractNumber,
    tracker_flow_id: input.trackerFlowId,
    document_labels: input.documentLabels,
    ...(input.workflowFlowId ? { flow_id: input.workflowFlowId } : {}),
  });

export const getPostAwardWorkspace = (id: string): Promise<ApiResponse<PostAwardWorkspace>> =>
  apiCall(workspacePath(id), 'GET');

export const listPostAwardWorkflows = (): Promise<ApiResponse<PostAwardWorkspace[]>> =>
  apiCall('/api/post-award-actions', 'GET');

export const getPostAwardWorkspaceByFlow = (flowId: string): Promise<ApiResponse<PostAwardWorkspace>> =>
  apiCall(`/api/post-award-actions/flows/${encodeURIComponent(flowId)}`, 'GET');

export const savePostAwardDraft = (id: string, input: PostAwardDraftInput): Promise<ApiResponse<PostAwardWorkspace>> =>
  apiCall(`${workspacePath(id)}/draft`, 'PATCH', {
    ...(input.entryMode !== undefined ? { entry_mode: input.entryMode } : {}),
    ...(input.contractNumber !== undefined ? { contract_number: input.contractNumber } : {}),
    ...(input.documentLabels !== undefined ? { document_labels: input.documentLabels } : {}),
    ...(input.narrative !== undefined ? { narrative: input.narrative } : {}),
    ...(input.selectedAction !== undefined ? { selected_action: input.selectedAction } : {}),
    ...(input.recommendationAnswer !== undefined ? { recommendation_answer: input.recommendationAnswer } : {}),
  });

export const recommendPostAwardRoute = (id: string, narrative: string, useSavedWork = true): Promise<ApiResponse<PostAwardWorkspace>> =>
  apiCall(`${workspacePath(id)}/route`, 'POST', { narrative, use_saved_work: useSavedWork });

export const prepareOptionSupport = (id: string, input: OptionSupportInput): Promise<ApiResponse<Record<string, unknown>>> =>
  apiCall(`${workspacePath(id)}/option-support`, 'POST', input);

/** Read the saved, typed, draft-only package. Absence is valid for legacy workspaces. */
export const getOptionPlanningWorkspace = (id: string): Promise<ApiResponse<OptionPlanningWorkspace | null>> =>
  apiCall(`${workspacePath(id)}/option-planning`, 'GET');

export const listOptionPlanningConfigurations = (id: string): Promise<ApiResponse<OptionPlanningConfigurationProjection[]>> =>
  apiCall(`${workspacePath(id)}/option-planning/configurations`, 'GET');

/** Save only declared, revisioned edits; configuration schema/body remains server-owned. */
export const patchOptionPlanningWorkspace = (id: string, input: OptionPlanningPatchInput): Promise<ApiResponse<OptionPlanningWorkspace>> =>
  apiCall(`${workspacePath(id)}/option-planning`, 'PATCH', {
    expected_workspace_revision: input.expectedWorkspaceRevision,
    updates: input.updates,
    ...(input.configurationId ? { configuration_id: input.configurationId } : {}),
    ...(input.configurationVersion ? { configuration_version: input.configurationVersion } : {}),
  });

export const prepareOptionPlanningModule = (id: string, moduleId: string): Promise<ApiResponse<OptionPlanningPrepareWorkspace>> =>
  apiCall(`${workspacePath(id)}/option-planning/modules/${encodeURIComponent(moduleId)}/prepare`, 'POST');

export const acknowledgeOptionPlanningModuleReview = (id: string, moduleId: string): Promise<ApiResponse<OptionPlanningWorkspace>> =>
  apiCall(`${workspacePath(id)}/option-planning/modules/${encodeURIComponent(moduleId)}/review`, 'POST', { review_acknowledged: true });

/** Start only the explicit, persisted no-options handoff for this package. */
export const startOptionPlanningRecompeteHandoff = (id: string, input: RecompeteHandoffInput): Promise<ApiResponse<{ successor_flow_id: string; draft_only: true }>> =>
  apiCall(`${workspacePath(id)}/option-planning/recompete-handoff`, 'POST', input);

export const optionPlanningArtifactDownloadUrl = (id: string, artifactId: string): string =>
  `${API_BASE_URL}${workspacePath(id)}/option-planning/artifacts/${encodeURIComponent(artifactId)}/download`;

/**
 * Fetch a server-owned working-draft artifact with the authenticated session.
 * The caller owns the short-lived browser anchor, which makes it possible for
 * the UI to display download progress and a useful error instead of relying on
 * a plain cross-origin anchor that can fail silently.
 */
export async function downloadOptionPlanningArtifact(
  id: string,
  artifact: Pick<OptionPlanningGeneratedArtifact, 'artifact_id' | 'filename'>,
): Promise<{ blob: Blob; filename: string }> {
  return downloadOptionPlanningFile(optionPlanningArtifactDownloadUrl(id, artifact.artifact_id), artifact.filename);
}

/** Render persisted selected form fields without preparing/storing a workflow artifact. */
export async function downloadSavedOptionPlanningModule(id: string, moduleId: string): Promise<{ blob: Blob; filename: string }> {
  return downloadOptionPlanningFile(`${API_BASE_URL}${workspacePath(id)}/option-planning/modules/${encodeURIComponent(moduleId)}/export`, 'option-planning-working-draft.pdf');
}

async function downloadOptionPlanningFile(url: string, fallbackFilename: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetchWithChatFeedback(url, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new Error(extractDetailMessage(body?.detail, 'Could not export the PDF.'));
  }

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quotedFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  let filename = fallbackFilename || 'option-planning-working-draft.pdf';
  if (encodedFilename) {
    try { filename = decodeURIComponent(encodedFilename); }
    catch { filename = encodedFilename; }
  } else if (quotedFilename) {
    filename = quotedFilename;
  }

  const blob = await response.blob();
  if (blob.size === 0) throw new Error('The PDF export was empty. Please try again.');
  return { blob, filename };
}

export const saveModificationFacts = (id: string, facts: ModificationFacts): Promise<ApiResponse<PostAwardWorkspace>> =>
  apiCall(`${workspacePath(id)}/modification-facts`, 'PUT', facts);

export const generateModificationPackage = (id: string, reviewAcknowledged = false): Promise<ApiResponse<DraftPackage>> =>
  reviewAcknowledged
    ? apiCall(`${workspacePath(id)}/modification-package`, 'POST', { review_acknowledged: true })
    : apiCall(`${workspacePath(id)}/modification-package`, 'POST');

export const startRecompeteHandoff = (id: string, input: RecompeteHandoffInput): Promise<ApiResponse<{ successor_flow_id: string; draft_only: true }>> =>
  apiCall(`${workspacePath(id)}/recompete-handoff`, 'POST', input);

/** Upload only to a saved draft workspace; this never issues or exercises an action. */
export async function uploadPostAwardDocument(
  workspaceId: string,
  file: File,
  documentType: PostAwardDocumentType,
): Promise<PostAwardDocumentUpload> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  const response = await fetchWithChatFeedback(`${API_BASE_URL}${workspacePath(workspaceId)}/documents`, {
    method: 'POST', body: formData, credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(extractDetailMessage(error.detail, `Upload failed with status ${response.status}`));
  }
  const payload = await response.json();
  const document = payload?.data?.source_documents?.slice().reverse().find(
    (source: { filename?: string }) => source.filename === file.name,
  );
  if (!payload?.success || !document) throw new Error('Upload completed without document metadata');
  return {
    document_id: document.document_id,
    filename: document.filename,
    extraction_status: 'extracted',
    provenance: document.provenance,
  };
}
